import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FolderKanban, LayoutTemplate, Palette, Music2, Settings as SettingsIcon, CreditCard, Plus, LogOut, Image as ImageIcon } from 'lucide-react';
import { Logo } from '../../components/marketing/Nav';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';

const NAVS = [
  { to: '/app', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/app/projects', label: 'Projects', icon: FolderKanban },
  { to: '/app/thumbnails', label: 'Thumbnails', icon: ImageIcon },
  { to: '/app/templates', label: 'Templates', icon: LayoutTemplate },
  { to: '/app/brand-kit', label: 'Brand Kit', icon: Palette },
  { to: '/app/audio', label: 'Audio', icon: Music2 },
  { to: '/app/settings', label: 'Settings', icon: SettingsIcon },
  { to: '/app/billing', label: 'Billing', icon: CreditCard },
];

function UsageMeter({ user }) {
  const pct = Math.min(100, Math.round((user.usage_minutes / user.usage_limit) * 100));
  return (
    <div className="px-3 py-3 rounded-xl border border-white/10 bg-[#12141D]">
      <div className="flex items-center justify-between text-xs mb-2">
        <span className="text-slate-400 font-mono uppercase tracking-widest">Usage</span>
        <span className="text-slate-300 font-mono">{user.usage_minutes} / {user.usage_limit} min</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-[#FF4D00] to-[#8B5CF6]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const doLogout = async () => { await logout(); nav('/'); };

  return (
    <div className="min-h-screen bg-[#090A0F] flex">
      <aside className="hidden md:flex flex-col w-60 shrink-0 border-r border-white/10 bg-[#0D0E14] fixed h-screen">
        <div className="h-16 flex items-center px-5 border-b border-white/10"><Logo /></div>
        <div className="p-3">
          <Button data-testid="sidebar-create-btn" onClick={() => nav('/app/create')} className="w-full rounded-xl h-11 font-semibold">
            <Plus className="w-4 h-4 mr-1" /> Create New Video
          </Button>
        </div>
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          {NAVS.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`sidebar-${n.label.toLowerCase().replace(' ', '-')}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? 'bg-[#FF4D00]/12 text-white border border-[#FF4D00]/30' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 space-y-3 border-t border-white/10">
          {user && <UsageMeter user={user} />}
          <div className="flex items-center gap-3 px-1">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF4D00] to-[#8B5CF6] grid place-items-center text-sm font-bold overflow-hidden">
              {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user?.name?.[0] || 'U').toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 uppercase font-mono">{user?.plan_name}</div>
            </div>
            <button onClick={doLogout} data-testid="logout-btn" className="text-slate-500 hover:text-white"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </aside>

      <div className="flex-1 md:ml-60 min-w-0">
        <div className="md:hidden h-14 flex items-center justify-between px-4 border-b border-white/10 bg-[#0D0E14] sticky top-0 z-40">
          <Logo />
          <Button size="sm" onClick={() => nav('/app/create')} className="rounded-full"><Plus className="w-4 h-4" /></Button>
        </div>
        <main className="p-5 sm:p-8 max-w-6xl mx-auto"><Outlet /></main>
      </div>
    </div>
  );
}
